import React from 'react';

import { Autocomplete, TextField } from '@mui/material';

import { useGetEmployeeUsers } from '~/hooks/apis/users/useGetEmployeeUsers';
import useDebounced from '~/hooks/useDebounced';

const MIN_SEARCH_LENGTH = 3;
const SEARCH_DELAY_MS = 300;

interface Props {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: string | null;
  excludedEmails: Array<string>;
  /**
   * Called when Enter is pressed and no suggestion is highlighted.
   * A highlighted suggestion keeps Autocomplete's own selection behaviour.
   * Omit this to leave Enter as a selection-only key, as the share dialog does.
   */
  onSubmit?: () => void;
}

export default function LensRecipientInput({ value, onChange, disabled, error, excludedEmails, onSubmit }: Props) {
  const search = useDebounced(value.trim(), SEARCH_DELAY_MS);
  const enabled = !disabled && search.length >= MIN_SEARCH_LENGTH;
  const users = useGetEmployeeUsers(search, enabled);
  const excluded = new Set(excludedEmails.map((email) => email.toLowerCase()));
  const options = enabled ? (users.data ?? []).filter((user) => !excluded.has(user.email.toLowerCase())) : [];
  const searchUnavailable =
    users.isError && enabled ? 'People search is unavailable. Enter a complete work email instead.' : null;
  const guidance =
    value.trim().length < MIN_SEARCH_LENGTH
      ? 'Type at least 3 characters to search, or enter a work email.'
      : 'Select a colleague, then choose Add.';
  // A validation error must not replace the search-down fallback. Both can be true at once:
  // the fallback tells the reader to type a full email, and the error says what is wrong with it.
  const helperText = [searchUnavailable, error ?? (searchUnavailable ? null : guidance)].filter(Boolean).join(' ');

  return (
    <Autocomplete
      freeSolo
      fullWidth
      disabled={disabled}
      options={options}
      value={value}
      inputValue={value}
      loading={enabled && users.isFetching}
      filterOptions={(entries) => entries}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.email)}
      onInputChange={(_, next, reason) => {
        if (reason === 'input' || reason === 'clear') onChange(next);
      }}
      onChange={(_, next) => onChange(typeof next === 'string' ? next : (next?.email ?? ''))}
      renderOption={(props, option) => (
        <li {...props} key={option.id_account}>
          {option.full_name} ({option.email})
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label="Name or email"
          error={Boolean(error)}
          helperText={helperText}
          inputProps={{
            ...params.inputProps,
            onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
              params.inputProps.onKeyDown?.(event);
              // Highlighted suggestions call preventDefault while selecting. Leave that path alone.
              if (!onSubmit || event.key !== 'Enter' || event.defaultPrevented || event.nativeEvent.isComposing) {
                return;
              }
              event.preventDefault();
              onSubmit();
            },
          }}
        />
      )}
    />
  );
}
